// Beginner Mode — a simplified, locked-progression wrapper around the
// 100-skill AI Mastery Path. State is stored in localStorage so brand
// new (often signed-out) visitors can progress without an account.
//
// Shape:
//   {
//     active: boolean              // is the guided shell currently on?
//     currentNum: number           // 1..100 — the skill the user is on
//     completed: number[]          // skill numbers the user has marked done
//     agentUnlockShown: boolean    // we only celebrate the agent unlock once
//   }
//
// We intentionally do NOT sync this to the database. Beginner Mode is a
// presentation-layer scaffold; once a user signs in and earns XP the
// canonical `useMasteryJourney` + server-side skills tracking takes over.
// Beginner Mode just makes sure the first 25 skills feel effortless.
import { useCallback, useEffect, useState } from "react";
import { AI_MASTERY_PATH } from "@/data/aiMasteryPath";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const STORAGE_KEY = "hl.beginnerMode.v1";
const AGENT_UNLOCK_AT = 25; // First skill of the Application tier
// Earlier "peek" at agents — fires the first time a beginner finishes
// skill #10. By this point they've done ~10 chats and can grok what an
// agent IS, but they're still 15 skills away from the deeper
// Application-tier unlock at #25. This earlier moment exists so users
// get a tangible "build something real" experience while still in the
// Awareness tier — keeps motivation high through the middle of the
// arc where drop-off is worst.
const FIRST_AGENT_PEEK_AT = 10;

/** Wizard pane the user is currently viewing inside BeginnerShell.
 *  Persisted so closing/refreshing the app drops them back on the
 *  exact step they left, not the start of the skill. */
export type WizardStep = "read" | "try" | "done" | "review";
const WIZARD_STEPS: WizardStep[] = ["read", "try", "done", "review"];

/**
 * Difficulty changes BOTH the granularity of the wizard (which panes
 * are shown) and the verbosity of the guidance copy. Persisted so the
 * user picks once and the whole journey adapts.
 *
 *  easy   — full hand-holding: all playbook steps shown, extra hints,
 *           micro-prompt visible, every pane (Read/Try/Done/Review).
 *  medium — current default: 3 playbook steps, standard copy, all panes.
 *  hard   — minimal scaffolding: 1-line summary, no Try pane, jump
 *           Read → Done so the user figures out the "how" themselves.
 */
export type Difficulty = "easy" | "medium" | "hard";
const DIFFICULTIES: Difficulty[] = ["easy", "medium", "hard"];

/**
 * Wizard step order per difficulty. Lives here (not in the shell) so
 * `setDifficulty` can atomically snap `currentStep` to the closest
 * still-visible step in the SAME write — preventing a transient render
 * where `currentStep` points at a hidden pane (e.g. "try" on hard).
 */
const STEP_ORDER_BY_DIFFICULTY: Record<Difficulty, WizardStep[]> = {
  easy: ["read", "try", "done", "review"],
  medium: ["read", "try", "done", "review"],
  hard: ["read", "done", "review"],
};

/** Returns the closest still-visible step for a given difficulty.
 *  Snaps FORWARD (so switching from try→hard lands on done, not back
 *  to read) so the user never feels like they lost progress. Falls
 *  back to the last visible step, then to "read". */
export function snapStepToDifficulty(
  step: WizardStep,
  difficulty: Difficulty,
): WizardStep {
  const order = STEP_ORDER_BY_DIFFICULTY[difficulty];
  if (order.includes(step)) return step;
  // Walk the FULL canonical order and pick the first step that's both
  // (a) at-or-after the current step, AND (b) visible in the new mode.
  const FULL: WizardStep[] = ["read", "try", "done", "review"];
  const fromIdx = Math.max(0, FULL.indexOf(step));
  for (let i = fromIdx; i < FULL.length; i++) {
    if (order.includes(FULL[i])) return FULL[i];
  }
  return order[order.length - 1] ?? "read";
}

export interface BeginnerState {
  active: boolean;
  currentNum: number;
  completed: number[];
  agentUnlockShown: boolean;
  /** Has the visitor seen the 3-step welcome modal at least once? */
  welcomeSeen: boolean;
  /**
   * Skill number that was JUST completed but whose recap interstitial
   * hasn't been dismissed yet. When set, the shell shows the
   * SkillRecapCard instead of advancing straight into the next skill.
   * Cleared on "Continue" or skip.
   */
  pendingRecapNum: number | null;
  /** Has the visitor seen the post-Beginner-Mode coachmark tour? */
  exitTourSeen: boolean;
  /**
   * Has the visitor seen the early "first agent peek" modal that fires
   * after completing skill #10? One-shot — never re-celebrate.
   */
  firstAgentPeekShown: boolean;
  /** Last wizard pane the user was on for `currentNum`. Persisted so
   *  reloading drops them back on the exact step (read/try/done/review). */
  currentStep: WizardStep;
  /** Hand-holding level. Defaults to "medium" (matches the prior UX). */
  difficulty: Difficulty;
}

const DEFAULT_STATE: BeginnerState = {
  active: false,
  currentNum: 1,
  completed: [],
  agentUnlockShown: false,
  welcomeSeen: false,
  pendingRecapNum: null,
  exitTourSeen: false,
  firstAgentPeekShown: false,
  currentStep: "read",
  difficulty: "easy",
};

function readState(): BeginnerState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    const stepCandidate =
      typeof parsed.currentStep === "string" &&
      (WIZARD_STEPS as string[]).includes(parsed.currentStep)
        ? (parsed.currentStep as WizardStep)
        : "read";
    return {
      active: !!parsed.active,
      currentNum: typeof parsed.currentNum === "number" ? parsed.currentNum : 1,
      completed: Array.isArray(parsed.completed) ? parsed.completed.filter((n: unknown) => typeof n === "number") : [],
      agentUnlockShown: !!parsed.agentUnlockShown,
      welcomeSeen: !!parsed.welcomeSeen,
      pendingRecapNum:
        typeof parsed.pendingRecapNum === "number" ? parsed.pendingRecapNum : null,
      exitTourSeen: !!parsed.exitTourSeen,
      firstAgentPeekShown: !!parsed.firstAgentPeekShown,
      currentStep: stepCandidate,
      difficulty:
        typeof parsed.difficulty === "string" &&
        (DIFFICULTIES as string[]).includes(parsed.difficulty)
          ? (parsed.difficulty as Difficulty)
          : "medium",
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function writeState(s: BeginnerState) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    // Notify other tabs / hook instances in the same tab.
    window.dispatchEvent(new CustomEvent("hl:beginnerMode:change"));
  } catch {
    /* quota / private mode — silently ignore */
  }
}

export const BEGINNER_AGENT_UNLOCK_AT = AGENT_UNLOCK_AT;
export const BEGINNER_FIRST_AGENT_PEEK_AT = FIRST_AGENT_PEEK_AT;

// Squads (multiplayer) is a higher-order feature — confusing for beginners
// who haven't yet built a personal practice. We hide every entry point
// (navbar quick action, home pillar, mastery rail collab stage, CTA banner)
// until the user has reached skill #15. Past that, all surfaces light up
// normally. Anyone with progress >= 15 OR currentNum >= 15 is "unlocked".
export const SQUADS_UNLOCK_AT = 15;

/** Returns true once the user has reached or passed the Squads unlock skill. */
export function useSquadsUnlocked(): boolean {
  const { completed, currentNum } = useBeginnerMode();
  const maxCompleted = completed.length > 0 ? Math.max(...completed) : 0;
  const reached = Math.max(maxCompleted, currentNum || 0);
  return reached >= SQUADS_UNLOCK_AT;
}

export function useBeginnerMode() {
  const { user } = useAuth();
  const [state, setState] = useState<BeginnerState>(() => readState());

  // Cross-instance sync: any setState() dispatches an event so every
  // mounted hook re-reads from storage. Cheap and avoids context.
  useEffect(() => {
    const onChange = () => setState(readState());
    window.addEventListener("hl:beginnerMode:change", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("hl:beginnerMode:change", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  // Cross-device hydration for signed-in users — read demonstrated
  // skills out of `user_mastery_skills` and union into the local
  // `completed[]`. We never DELETE locally-completed entries during
  // this merge so the user keeps anything they did while signed out.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("user_mastery_skills")
        .select("skill_num")
        .eq("user_id", user.id);
      if (cancelled || error || !data) return;
      const cur = readState();
      const merged = Array.from(
        new Set([...cur.completed, ...data.map((r) => r.skill_num)]),
      ).sort((a, b) => a - b);
      if (merged.length === cur.completed.length) return;
      const next: BeginnerState = { ...cur, completed: merged };
      writeState(next);
      setState(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const update = useCallback((patch: Partial<BeginnerState>) => {
    const next = { ...readState(), ...patch };
    writeState(next);
    setState(next);
  }, []);

  const start = useCallback(
    (atSkill?: number) => {
      const cur = readState();
      const target =
        typeof atSkill === "number"
          ? Math.max(1, Math.min(atSkill, AI_MASTERY_PATH.length))
          : cur.currentNum || 1;
      // If we're snapping to a different skill we also reset the step;
      // otherwise keep the persisted pane so "Resume" lands exactly
      // where the user left off (e.g. mid-Try).
      const keepStep = target === cur.currentNum;
      update({
        active: true,
        currentNum: target,
        pendingRecapNum: null,
        currentStep: keepStep ? cur.currentStep : "read",
      });
    },
    [update],
  );

  const exit = useCallback(() => update({ active: false }), [update]);

  const markDone = useCallback(() => {
    const cur = readState();
    const completed = cur.completed.includes(cur.currentNum)
      ? cur.completed
      : [...cur.completed, cur.currentNum];
    // Don't advance currentNum yet — show the recap interstitial first.
    // `continueAfterRecap` does the actual advance.
    update({ completed, pendingRecapNum: cur.currentNum, currentStep: "review" });
    // Fire-and-forget DB write for signed-in users so progress syncs
    // across devices. The table's primary key (user_id, skill_num)
    // makes this idempotent — re-marking is a no-op insert.
    if (user && cur.currentNum >= 1 && cur.currentNum <= 100) {
      supabase
        .from("user_mastery_skills")
        .insert({
          user_id: user.id,
          skill_num: cur.currentNum,
          source: "manual",
        })
        .then(({ error }) => {
          if (error && error.code !== "23505") {
            // 23505 = unique_violation — expected when re-marking.
            // eslint-disable-next-line no-console
            console.warn("[beginnerMode] mastery insert failed", error);
          }
        });
    }
  }, [update, user]);

  /** Dismiss the recap and move to the next skill. */
  const continueAfterRecap = useCallback(() => {
    const cur = readState();
    const base = cur.pendingRecapNum ?? cur.currentNum;
    const nextNum = Math.min(base + 1, AI_MASTERY_PATH.length);
    update({ pendingRecapNum: null, currentNum: nextNum, currentStep: "read" });
  }, [update]);

  const goTo = useCallback(
    (num: number) => {
      const clamped = Math.max(1, Math.min(num, AI_MASTERY_PATH.length));
      // Switching skills always restarts the wizard at "read".
      update({ currentNum: clamped, currentStep: "read" });
    },
    [update],
  );

  /** Persist the wizard pane the user is currently viewing. Called
   *  by BeginnerShell whenever the user advances or revisits a step. */
  const setStep = useCallback(
    (step: WizardStep) => {
      update({ currentStep: step });
    },
    [update],
  );

  /** Change the wizard difficulty. Persisted; takes effect immediately
   *  on the current skill so the user sees the change without reload.
   *  Also snaps `currentStep` to the closest visible pane in the new
   *  difficulty's step order — same write, no transient invalid state. */
  const setDifficulty = useCallback(
    (difficulty: Difficulty) => {
      const cur = readState();
      const snapped = snapStepToDifficulty(cur.currentStep, difficulty);
      update({ difficulty, currentStep: snapped });
    },
    [update],
  );

  /** Jump to the first skill the user hasn't completed yet and reset
   *  the wizard to "read". Used by the "Resume where I left off" chip
   *  when the user has fallen behind their actual progress. */
  const resume = useCallback(() => {
    const cur = readState();
    const sortedDone = new Set(cur.completed);
    let firstIncomplete = AI_MASTERY_PATH.length;
    for (const skill of AI_MASTERY_PATH) {
      if (!sortedDone.has(skill.num)) {
        firstIncomplete = skill.num;
        break;
      }
    }
    update({
      active: true,
      currentNum: firstIncomplete,
      currentStep: "read",
      pendingRecapNum: null,
    });
  }, [update]);

  const dismissAgentUnlock = useCallback(
    () => update({ agentUnlockShown: true }),
    [update],
  );

  const dismissFirstAgentPeek = useCallback(
    () => update({ firstAgentPeekShown: true }),
    [update],
  );

  const markWelcomeSeen = useCallback(
    () => update({ welcomeSeen: true }),
    [update],
  );

  const markExitTourSeen = useCallback(
    () => update({ exitTourSeen: true }),
    [update],
  );

  const reset = useCallback(() => {
    writeState(DEFAULT_STATE);
    setState(DEFAULT_STATE);
  }, []);

  const currentSkill =
    AI_MASTERY_PATH.find((s) => s.num === state.currentNum) ?? AI_MASTERY_PATH[0];
  const justUnlockedAgent =
    state.completed.includes(AGENT_UNLOCK_AT) && !state.agentUnlockShown;
  // Earlier peek — fires once after #10 is marked done, but only if the
  // user hasn't already blown past the deeper #25 unlock (avoid
  // re-celebrating for users who joined late or imported progress).
  const justUnlockedFirstAgentPeek =
    state.completed.includes(FIRST_AGENT_PEEK_AT) &&
    !state.firstAgentPeekShown &&
    !state.completed.includes(AGENT_UNLOCK_AT);

  return {
    ...state,
    currentSkill,
    justUnlockedAgent,
    justUnlockedFirstAgentPeek,
    start,
    exit,
    markDone,
    continueAfterRecap,
    goTo,
    setStep,
    setDifficulty,
    resume,
    dismissAgentUnlock,
    dismissFirstAgentPeek,
    markWelcomeSeen,
    markExitTourSeen,
    reset,
  };
}
